import React, { useEffect, useState } from 'react';
import { App as AntdApp, Card, Row, Col, Button, Space, Tag, Badge, Form, Input, Empty, Spin, Popconfirm, Checkbox, theme } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined, SearchOutlined, EnvironmentOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { trpc } from '../../lib/trpc';
import { IconPicker } from '../../components/IconPicker';
import { withDevModalFilePath, HexColorPicker } from '../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/settings/LocationCategoriesTab.tsx');

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof EnvironmentOutlined>>;

interface LocationCategory {
  id: string;
  companyId: string;
  categoryLabel: string;
  description?: string | null;
  isBuilding?: boolean;
  displayOrder: number;
  color?: string | null;
  icon?: string | null;
  isActive: boolean;
  usageCount?: number;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const sanitizeDescriptionValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const SortableItem: React.FC<{
  category: LocationCategory;
  isSelected: boolean;
  onClick: () => void;
  getIconNode: (iconName?: string | null) => React.ReactNode;
  token: ReturnType<typeof theme.useToken>['token'];
}> = ({ category, isSelected, onClick, getIconNode, token }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'pointer',
    backgroundColor: isSelected ? token.colorPrimaryBg : 'transparent',
    padding: '12px',
    borderRadius: '4px',
    opacity: isDragging ? 0.5 : 1,
  } as const;

  const resolvedColor = category.color ?? '#ffffff';

  return (
    <div ref={setNodeRef} style={style} onClick={onClick}>
      <Space>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
          <HolderOutlined style={{ color: token.colorTextTertiary, fontSize: 16 }} />
        </div>
        {category.icon ? getIconNode(category.icon) : <EnvironmentOutlined />}
        <div
          style={{
            width: 16,
            height: 16,
            backgroundColor: resolvedColor,
            borderRadius: '2px',
            border: `1px solid ${token.colorBorder}`,
          }}
        />
        <strong>{category.categoryLabel}</strong>
        <Tag color={category.isActive ? 'green' : 'default'}>
          {category.isActive ? '有効' : '無効'}
        </Tag>
        {category.isBuilding && (
          <Tag color="blue">建物</Tag>
        )}
        {category.usageCount !== undefined && category.usageCount > 0 && (
          <Badge count={category.usageCount} showZero color="green" />
        )}
      </Space>
    </div>
  );
};

const LocationCategoriesTab: React.FC = () => {
  const { token } = theme.useToken();
  const { message: messageApi } = AntdApp.useApp();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<LocationCategory | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();
  const utils = trpc.useUtils();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: categories = [], isLoading } = trpc.locationCategory.listWithUsage.useQuery({
    includeInactive: true,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const [orderedCategories, setOrderedCategories] = useState<LocationCategory[]>([]);

  useEffect(() => {
    const normalizedCategories = categories as unknown as LocationCategory[];
    setOrderedCategories(normalizedCategories);
    if (selectedCategory) {
      const refreshed =
        normalizedCategories.find((c) => c.id === selectedCategory.id) ?? null;
      setSelectedCategory(refreshed);
    }
  }, [categories, selectedCategory]);

  const filteredCategories = orderedCategories.filter(
    (category) =>
      !searchText ||
      category.categoryLabel.toLowerCase().includes(searchText.toLowerCase())
  );

  const reorderMutation = trpc.locationCategory.reorder.useMutation();

  const createMutation = trpc.locationCategory.create.useMutation({
    onSuccess: async () => {
      messageApi.success('エリアカテゴリを作成しました');
      await utils.locationCategory.listWithUsage.invalidate();
      setIsCreateModalVisible(false);
      form.resetFields();
    },
    onError: (error) => {
      const errorCode = (error as { data?: { code?: string } } | undefined)?.data?.code;
      if (errorCode === 'CONFLICT') {
        form.setFields([{ name: 'categoryLabel', errors: [error.message] }]);
      }
      messageApi.error(error.message || 'エリアカテゴリの作成に失敗しました');
    },
  });

  const updateMutation = trpc.locationCategory.update.useMutation({
    onSuccess: async (updatedCategory) => {
      messageApi.success('エリアカテゴリを更新しました');
      await utils.locationCategory.listWithUsage.invalidate();
      setSelectedCategory(updatedCategory as unknown as LocationCategory);
      setIsEditMode(false);
    },
    onError: (error) => {
      const errorCode = (error as { data?: { code?: string } } | undefined)?.data?.code;
      if (errorCode === 'CONFLICT') {
        form.setFields([{ name: 'categoryLabel', errors: [error.message] }]);
      }
      messageApi.error(error.message || 'エリアカテゴリの更新に失敗しました');
    },
  });

  const deleteMutation = trpc.locationCategory.delete.useMutation({
    onSuccess: async () => {
      messageApi.success('エリアカテゴリを削除しました');
      await utils.locationCategory.listWithUsage.invalidate();
      setSelectedCategory(null);
    },
    onError: (error) => {
      messageApi.error(error.message || 'エリアカテゴリの削除に失敗しました');
    },
  });

  const getIconNode = (iconName?: string | null): React.ReactNode => {
    if (!iconName || !(iconName in Icons)) {
      return null;
    }
    const iconMap = Icons as unknown as Record<string, AntIconComponent>;
    const IconComponent = iconMap[iconName];
    if (!IconComponent) {
      return null;
    }
    return <IconComponent />;
  };

  const handleSelectCategory = (category: LocationCategory) => {
    setSelectedCategory(category);
    setIsEditMode(false);
    form.setFieldsValue({
      categoryLabel: category.categoryLabel,
      description: sanitizeDescriptionValue(category.description),
      displayOrder: category.displayOrder,
      color: category.color || '#ffffff',
      icon: category.icon || undefined,
      isActive: category.isActive,
      isBuilding: category.isBuilding ?? false,
    });
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
      const colorValue = resolvedColor ?? '#ffffff';
      await createMutation.mutateAsync({
        categoryLabel: values.categoryLabel,
        description: values.description || undefined,
        color: colorValue,
        icon: values.icon,
        displayOrder: values.displayOrder ?? 0,
        isBuilding: values.isBuilding ?? false,
      });
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      messageApi.error('入力内容を確認してください');
    }
  };

  const handleUpdate = async () => {
    if (!selectedCategory) return;

    try {
      const values = await form.validateFields();
      const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
      const colorValue = resolvedColor ?? '#ffffff';
      await updateMutation.mutateAsync({
        id: selectedCategory.id,
        categoryLabel: values.categoryLabel,
        description: values.description || undefined,
        color: colorValue,
        icon: values.icon,
        displayOrder: values.displayOrder ?? selectedCategory.displayOrder,
        isActive: values.isActive,
        isBuilding: values.isBuilding,
      });
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      messageApi.error('入力内容を確認してください');
    }
  };

  const handleDelete = async (categoryId: string) => {
    await deleteMutation.mutateAsync({ id: categoryId });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedCategories.findIndex((item) => item.id === active.id);
    const newIndex = orderedCategories.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const previousItems = orderedCategories;
    const nextItems = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(nextItems);

    reorderMutation.mutate(
      {
        items: nextItems.map((category, index) => ({
          id: category.id,
          displayOrder: index,
        })),
      },
      {
        onSuccess: async () => {
          messageApi.success('表示順序を更新しました');
          await utils.locationCategory.listWithUsage.invalidate();
        },
        onError: (error) => {
          setOrderedCategories(previousItems);
          messageApi.error(error.message || '表示順序の更新に失敗しました');
        },
      }
    );
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={10}>
        <Card
          title="エリアカテゴリ一覧"
          extra={
            <Space>
              <Input
                placeholder="検索..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: 200 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setIsCreateModalVisible(true);
                  form.resetFields();
                  form.setFieldsValue({ color: '#ffffff', isBuilding: false });
                }}
              >
                新規作成
              </Button>
            </Space>
          }
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : filteredCategories.length === 0 ? (
            <Empty description="カテゴリがありません" />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={filteredCategories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {filteredCategories.map((category) => (
                  <SortableItem
                    key={category.id}
                    category={category as unknown as LocationCategory}
                    isSelected={selectedCategory?.id === category.id}
                    onClick={() => handleSelectCategory(category as unknown as LocationCategory)}
                    getIconNode={getIconNode}
                    token={token}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Card>
      </Col>

      <Col xs={24} md={14}>
        <Card
          title="カテゴリ詳細"
          extra={
            selectedCategory && (
              <Space>
                {!isEditMode ? (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditMode(true)}
                  >
                    編集
                  </Button>
                ) : (
                  <>
                    <Button
                      type="primary"
                      onClick={handleUpdate}
                      loading={updateMutation.isPending}
                    >
                      保存
                    </Button>
                    <Button onClick={() => setIsEditMode(false)}>キャンセル</Button>
                  </>
                )}
                <Popconfirm
                  title="本当に削除しますか？"
                  description={
                    selectedCategory.usageCount && selectedCategory.usageCount > 0
                      ? `${selectedCategory.usageCount}件のエリアがこのカテゴリを使用しています。`
                      : undefined
                  }
                  onConfirm={() => handleDelete(selectedCategory.id)}
                  okText="削除"
                  cancelText="キャンセル"
                  disabled={!!selectedCategory.usageCount && selectedCategory.usageCount > 0}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!!selectedCategory.usageCount && selectedCategory.usageCount > 0}
                  >
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
        >
          {!selectedCategory ? (
            <Empty description="左のリストからカテゴリを選択してください" />
          ) : (
            <Form form={form} layout="vertical" disabled={!isEditMode}>
              <Form.Item
                label="カテゴリ名"
                name="categoryLabel"
                rules={[
                  { required: true, message: 'カテゴリ名を入力してください' },
                  { max: 255, message: 'カテゴリ名は255文字以内で入力してください' },
                ]}
              >
                <Input placeholder="例: 工場" />
              </Form.Item>

              <Form.Item label="説明" name="description">
                <Input.TextArea placeholder="カテゴリの説明（任意）" rows={3} />
              </Form.Item>

              <Form.Item label="カラー" name="color">
                <HexColorPicker />
              </Form.Item>

              <Form.Item label="アイコン" name="icon">
                <IconPicker placeholder="アイコンを選択（任意）" />
              </Form.Item>

              <Form.Item label="ステータス" name="isActive" valuePropName="checked">
                <Checkbox disabled={!isEditMode}>有効</Checkbox>
              </Form.Item>

              <Form.Item label="建物カテゴリ" name="isBuilding" valuePropName="checked">
                <Checkbox disabled={!isEditMode}>法定点検対象の建物カテゴリとして扱う</Checkbox>
              </Form.Item>

              {selectedCategory.usageCount !== undefined && (
                <Form.Item label="使用数">
                  <Badge count={selectedCategory.usageCount} showZero color="green" />
                  <span style={{ marginLeft: 8 }}>件のエリアがこのカテゴリを使用しています</span>
                </Form.Item>
              )}

              <div style={{ padding: '12px', backgroundColor: token.colorFillQuaternary, borderRadius: '4px', marginTop: '16px' }}>
                <Space direction="vertical" size="small">
                  <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                    ヒント: 表示順序の変更方法
                  </div>
                  <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                    左のリストでカテゴリをドラッグ&ドロップして並び替えできます
                  </div>
                </Space>
              </div>
            </Form>
          )}
        </Card>
      </Col>

      <Modal
        title="新しいエリアカテゴリを作成"
        open={isCreateModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={createMutation.isPending}
        okText="作成"
        cancelText="キャンセル"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="カテゴリ名"
            name="categoryLabel"
            rules={[
              { required: true, message: 'カテゴリ名を入力してください' },
              { max: 255, message: '255文字以内で入力してください' },
            ]}
          >
            <Input placeholder="例: 倉庫" />
          </Form.Item>

          <Form.Item label="説明" name="description">
            <Input.TextArea placeholder="カテゴリの説明（任意）" rows={3} />
          </Form.Item>

          <Form.Item label="カラー" name="color">
            <HexColorPicker />
          </Form.Item>

          <Form.Item label="アイコン" name="icon" extra="カテゴリを視覚的に識別するためのアイコン">
            <IconPicker placeholder="アイコンを選択（任意）" />
          </Form.Item>

          <Form.Item label="建物カテゴリ" name="isBuilding" valuePropName="checked">
            <Checkbox>法定点検対象の建物カテゴリとして扱う</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default LocationCategoriesTab;
